import React from 'react';
import {
  Box,
  Button,
  CloseButton,
  Flex,
  Icon,
  Text,
  useColorModeValue,
  VStack,
} from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import { MdNotificationsActive } from 'react-icons/md';
import { setPushPromptDeclinedCookie } from 'utils/pushNotificationBannerCookie';

export type PushNotificationPromptProps = {
  onEnable: () => void;
  onClose: () => void;
};

/**
 * Banner flotante glassmorphism para solicitar permiso de notificaciones del navegador.
 */
const PushNotificationPrompt: React.FC<PushNotificationPromptProps> = ({ onEnable, onClose }) => {
  const { t } = useTranslation();
  const shellBg = useColorModeValue(
    'linear-gradient(145deg, rgba(255,255,255,0.72) 0%, rgba(240,249,255,0.55) 45%, rgba(224,242,254,0.5) 100%)',
    'linear-gradient(145deg, rgba(30,41,59,0.82) 0%, rgba(15,23,42,0.78) 50%, rgba(30,58,78,0.72) 100%)'
  );
  const borderColor = useColorModeValue('cyan.200', 'cyan.500');
  const titleColor = useColorModeValue('gray.800', 'white');
  const descColor = useColorModeValue('gray.600', 'gray.300');
  const iconBg = useColorModeValue('cyan.50', 'whiteAlpha.100');
  const iconColor = useColorModeValue('cyan.600', 'cyan.300');
  const shadow = useColorModeValue(
    '0 24px 64px rgba(14, 116, 144, 0.18), 0 0 0 1px rgba(125, 211, 252, 0.35), inset 0 1px 0 rgba(255,255,255,0.85)',
    '0 28px 72px rgba(0,0,0,0.45), 0 0 0 1px rgba(34, 211, 238, 0.25), inset 0 1px 0 rgba(255,255,255,0.08)'
  );
  const closeHoverBg = useColorModeValue('blackAlpha.100', 'whiteAlpha.200');

  const handleDeny = () => {
    setPushPromptDeclinedCookie();
    onClose();
  };

  return (
    <Box
      role="dialog"
      aria-labelledby="orgcloud-push-prompt-title"
      aria-describedby="orgcloud-push-prompt-desc"
      position="fixed"
      bottom={{ base: 3, md: 6 }}
      left="50%"
      transform="translateX(-50%)"
      zIndex={1000}
      w={{ base: 'calc(100% - 24px)', md: 'auto' }}
      maxW={{ base: '100%', md: 'min(480px, calc(100vw - 48px))' }}
      px={0}
    >
      <Flex
        position="relative"
        direction={{ base: 'column', sm: 'row' }}
        align={{ base: 'stretch', sm: 'center' }}
        gap={{ base: 3, sm: 4 }}
        p={{ base: 4, md: 5 }}
        borderRadius={{ base: '20px', md: '22px' }}
        borderWidth="1px"
        borderColor={borderColor}
        bg={shellBg}
        backdropFilter="saturate(180%) blur(20px)"
        sx={{
          WebkitBackdropFilter: 'saturate(180%) blur(20px)',
          boxShadow: shadow,
        }}
      >
        <Flex
          align="center"
          justify="center"
          w={{ base: '48px', sm: '52px' }}
          h={{ base: '48px', sm: '52px' }}
          minW={{ base: '48px', sm: '52px' }}
          borderRadius="16px"
          bg={iconBg}
          alignSelf={{ base: 'flex-start', sm: 'center' }}
        >
          <Icon as={MdNotificationsActive} boxSize={7} color={iconColor} aria-hidden />
        </Flex>
        <VStack align="stretch" spacing={2} flex={1} minW={0}>
          <Text
            id="orgcloud-push-prompt-title"
            fontSize={{ base: 'md', md: 'lg' }}
            fontWeight="800"
            color={titleColor}
            lineHeight="short"
          >
            {t('Notificaciones push OrgCloud')}
          </Text>
          <Text id="orgcloud-push-prompt-desc" fontSize={{ base: 'sm', md: 'md' }} color={descColor} lineHeight="tall">
            {t('Recibe avisos importantes en tu navegador. Puedes cambiarlo cuando quieras en la configuración del navegador.')}
          </Text>
          <Flex
            gap={2}
            flexWrap="wrap"
            pt={1}
            justify={{ base: 'stretch', sm: 'flex-end' }}
            w="100%"
          >
            <Button
              size="sm"
              variant="ghost"
              minH={{ base: '44px', sm: '36px' }}
              colorScheme="gray"
              onClick={handleDeny}
              flex={{ base: 1, sm: 'unset' }}
            >
              {t('No, gracias')}
            </Button>
            <Button
              size="sm"
              minH={{ base: '44px', sm: '36px' }}
              colorScheme="cyan"
              fontWeight="700"
              onClick={onEnable}
              flex={{ base: 1, sm: 'unset' }}
              boxShadow="0 4px 14px rgba(6, 182, 212, 0.35)"
              _hover={{ boxShadow: '0 6px 20px rgba(6, 182, 212, 0.45)' }}
            >
              {t('Habilitar notificaciones')}
            </Button>
          </Flex>
        </VStack>
        <CloseButton
          position="absolute"
          top={2}
          right={2}
          size="sm"
          rounded="full"
          color={descColor}
          _hover={{ bg: closeHoverBg }}
          aria-label={t('Cerrar')}
          onClick={handleDeny}
        />
      </Flex>
    </Box>
  );
};

export default PushNotificationPrompt;
